import React from 'react';
import { Alert } from 'react-bootstrap';
import { SectionLoader } from '../../Components/LoadingSpinner';
import { useProject } from '../../hooks/projects/useProject';
import { useProjectWorkOrders } from '../../hooks/projects/useProjectWorkOrders';
import { useFirebaseImage, getImagePathWithSize, ImageSize } from '../../Helpers/ImageLoader';
import { placeholderForBuildingType } from '../../Helpers/PlaceholderUtils';
import { BuildingType } from '../../types/BuildingTypeEnums';
import { GroupTag } from '../../types/protocols';
import {
    ProjectHeaderSection,
    ProjectDescriptionSection,
    ProjectDatesSection,
    ProjectBudgetSection,
    ProjectPhotosSection,
    ProjectNotesSection,
    ProjectChecklistsSection,
    ProjectAttachmentsSection,
    ProjectWorkOrdersSection,
    ProjectCostsSection,
    ProjectExternalContactsSection,
    ProjectAssignmentField,
} from './components';
import { EditableUserField } from '../../Components/EditableUserField';
import { UserCheck, UserPlus } from 'lucide-react';
import { useExternalUsers } from '../../hooks/useExternalUsers';
import { getAuth } from 'firebase/auth';
import { useUsersData } from '../../Helpers/UsersContext';

interface ProjectPreviewPaneProps {
    buildingKey: string;
    projectKey: string;
    buildingName?: string;
    buildingType?: BuildingType;
    buildingCity?: string;
    buildingMunicipality?: string;
    buildingGroupTags?: { [key: string]: GroupTag };
    buildingCoverPhotoPath?: string;
    onProjectDeleted: () => void;
}

export const ProjectPreviewPane: React.FC<ProjectPreviewPaneProps> = ({
    buildingKey,
    projectKey,
    buildingName,
    buildingType,
    buildingCity,
    buildingMunicipality,
    buildingGroupTags,
    buildingCoverPhotoPath,
    onProjectDeleted,
}) => {
    const {
        project,
        loading,
        error,
        submitting,

        updateTitle,
        updateDescription,
        updateStatus,
        reopenProject,
        updateStartDate,
        updatePlannedEndDate,
        updateActualEndDate,
        updateBudgetAllocation,
        deleteProject,

        assignProject,
        addProjectAssignee,
        removeProjectAssignee,
        updateResponsibleOrderer,
        updateSecondaryOrderer,

        addNote,
        updateNote,
        deleteNote,

        addChecklist,
        updateChecklistTitle,
        deleteChecklist,
        addChecklistItem,
        updateChecklistItemText,
        toggleChecklistItem,
        deleteChecklistItem,
        reorderChecklistItems,

        uploadPhotos,
        deletePhoto,

        uploadAttachment,
        deleteAttachment,
        renameAttachment,

        addExternalContact,
        removeExternalContact,
        updateExternalContactNote,

        clearError,
        refetch,
    } = useProject({ buildingKey, projectKey });

    // Fetch external users for contact lookup
    const { users: externalUsers, refetch: refetchExternalUsers } = useExternalUsers();

    const { users: appUsers } = useUsersData();
    const currentUserId = getAuth().currentUser?.uid;
    const isAdmin = appUsers?.find((u) => u.key === currentUserId)?.admin ?? false;

    // Internal users assignable to the project (matches work-order assignment filter)
    const internalUsers = appUsers?.filter((u) => u.accessToWeb && !u.deleted && !u.isExternal) || [];

    // Resolve an assignee UID (internal or external) to a display name
    const getUserName = (uid: string): string => {
        const internal = appUsers?.find((u) => u.key === uid);
        if (internal) return internal.name || internal.email || uid;
        const external = externalUsers?.find((u) => u.uid === uid);
        if (external) return external.name || external.email || uid;
        return uid;
    };

    // Fetch work orders for this project (needed for costs section budget summary)
    const {
        workOrders: linkedWorkOrders,
        loading: workOrdersLoading
    } = useProjectWorkOrders(buildingKey, projectKey);

    const thumbnailPath = getImagePathWithSize(buildingCoverPhotoPath, ImageSize.THUMBNAIL_SMALL);
    const placeholder = placeholderForBuildingType(buildingType);
    const { src: coverPhotoSrc } = useFirebaseImage(thumbnailPath, placeholder);

    const handleDelete = async () => {
        await deleteProject();
        onProjectDeleted();
    };

    if (loading) {
        return <SectionLoader />;
    }

    if (!project) {
        return (
            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#666',
                fontSize: '18px',
                padding: '20px',
                textAlign: 'center'
            }}>
                Projektet hittades inte
            </div>
        );
    }

    return (
        <div className="project-preview-scroll">
            <div className="std-box project-preview-card">
                {error && (
                    <Alert variant="danger" dismissible onClose={clearError} className="mb-3">
                        {error}
                    </Alert>
                )}

                {/* Header Section (Title + Building Info + Status + Delete) */}
                <ProjectHeaderSection
                    project={project}
                    onUpdateTitle={updateTitle}
                    onUpdateStatus={updateStatus}
                    onReopenProject={reopenProject}
                    onDelete={handleDelete}
                    submitting={submitting}
                    buildingName={buildingName}
                    buildingCity={buildingCity}
                    buildingMunicipality={buildingMunicipality}
                    buildingGroupTags={buildingGroupTags}
                    coverPhotoSrc={coverPhotoSrc}
                />

                {/* Description Section (above dates) */}
                <ProjectDescriptionSection
                    project={project}
                    onUpdateDescription={updateDescription}
                    submitting={submitting}
                />

                {/* Dates + assignment metadata row */}
                <ProjectDatesSection
                    project={project}
                    onUpdateStartDate={updateStartDate}
                    onUpdatePlannedEndDate={updatePlannedEndDate}
                    onUpdateActualEndDate={updateActualEndDate}
                    submitting={submitting}
                >
                    {/* Beställare (informational) — same row/style as the dates */}
                    <EditableUserField
                        value={project.responsibleOrderer}
                        onSave={updateResponsibleOrderer}
                        users={internalUsers}
                        getUserName={getUserName}
                        label="Ansvarig beställare"
                        icon={<UserCheck size={14} />}
                        disabled={submitting}
                    />
                    <EditableUserField
                        value={project.secondaryOrderer}
                        onSave={updateSecondaryOrderer}
                        users={internalUsers}
                        getUserName={getUserName}
                        label="Sekundär beställare"
                        icon={<UserPlus size={14} />}
                        disabled={submitting}
                    />
                    <ProjectAssignmentField
                        project={project}
                        buildingKey={buildingKey}
                        internalUsers={internalUsers}
                        externalUsers={externalUsers}
                        isAdmin={isAdmin}
                        getUserName={getUserName}
                        onAssignInternal={assignProject}
                        onAddInternal={addProjectAssignee}
                        onRemoveInternal={removeProjectAssignee}
                        onProjectChanged={refetch}
                        onRefetchUsers={refetchExternalUsers}
                    />
                </ProjectDatesSection>

                {/* Budget Section */}
                <ProjectBudgetSection
                    project={project}
                    onUpdateBudgetAllocation={updateBudgetAllocation}
                    submitting={submitting}
                />

                {/* Work Orders Section (Delmoment) */}
                <ProjectWorkOrdersSection
                    buildingKey={buildingKey}
                    projectKey={projectKey}
                />

                {/* Photos Section */}
                <ProjectPhotosSection
                    project={project}
                    projectKey={projectKey}
                    onUploadPhotos={uploadPhotos}
                    onDeletePhoto={deletePhoto}
                    submitting={submitting}
                />

                {/* Notes Section */}
                <ProjectNotesSection
                    project={project}
                    onAddNote={addNote}
                    onUpdateNote={updateNote}
                    onDeleteNote={deleteNote}
                    submitting={submitting}
                />

                {/* Checklists Section */}
                <ProjectChecklistsSection
                    project={project}
                    onAddChecklist={addChecklist}
                    onUpdateChecklistTitle={updateChecklistTitle}
                    onDeleteChecklist={deleteChecklist}
                    onAddChecklistItem={addChecklistItem}
                    onUpdateChecklistItemText={updateChecklistItemText}
                    onToggleChecklistItem={toggleChecklistItem}
                    onDeleteChecklistItem={deleteChecklistItem}
                    onReorderChecklistItems={reorderChecklistItems}
                    submitting={submitting}
                />

                {/* Attachments Section */}
                <ProjectAttachmentsSection
                    project={project}
                    onUploadAttachment={(file, displayName) => uploadAttachment(file, displayName, { refresh: false })}
                    onUploadsComplete={refetch}
                    onDeleteAttachment={deleteAttachment}
                    onRenameAttachment={renameAttachment}
                    submitting={submitting}
                />

                {/* External Contacts Section */}
                <ProjectExternalContactsSection
                    project={project}
                    externalUsers={externalUsers}
                    internalUsers={internalUsers}
                    getUserName={getUserName}
                    onAddContact={addExternalContact}
                    onRemoveContact={removeExternalContact}
                    onEditNote={updateExternalContactNote}
                    onRefetchUsers={refetchExternalUsers}
                    submitting={submitting}
                    isAdmin={isAdmin}
                />

                {/* Budget Summary (Budget vs work-order actual cost) */}
                <ProjectCostsSection
                    project={project}
                    linkedWorkOrders={linkedWorkOrders}
                />
            </div>
        </div>
    );
};
